'use client';

import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { Fragment, useEffect } from 'react';

import type { NavMenuItemType } from '@/app/types/top-navigation';
import { safeCall } from '@/app/utils/safeCall';
import { List } from '@/components/layouts/top-navigation/shared/menu-item/base/list';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';

const ToggleDesktop = dynamic(
  () =>
    import(
      '@/components/layouts/top-navigation/shared/menu-item/base/toggle/desktop'
    ).then((mod) => mod.ToggleDesktop),
  {
    ssr: false,
  }
);
const ToggleMobile = dynamic(
  () =>
    import(
      '@/components/layouts/top-navigation/shared/menu-item/base/toggle/mobile'
    ).then((mod) => mod.ToggleMobile),
  {
    ssr: false,
  }
);

export interface BaseDisplayConfigs {
  isModelBuyingTools?: boolean;
  isDesktop?: boolean;
}

export type BaseRenderOptions = 'grid' | 'expand';

interface BaseProps extends BaseDisplayConfigs {
  as?: BaseRenderOptions;
  items?: NavMenuItemType[];
  onReady?: () => void;
  onClose?: () => void;
}

interface WrapperProps {
  children: ReactNode;
  isBuyingToolsAndDesktop?: boolean;
}

const Wrapper = ({ children }: WrapperProps) => {
  return (
    <Container
      size="desktop-xl-only"
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        py: '$space-6',
        '@md': {
          py: '$space-10',
        },
        '@xl': {
          py: '$space-0 !important', // Note: Handle stitches media query order priority bug
          gridTemplateColumns: 'repeat(12, 1fr)',
          columnGap: '$space-6',
          rowGap: '$space-8',
        },
      }}
    >
      {children}
    </Container>
  );
};

export const Base = ({
  items = [],
  isModelBuyingTools = false,
  isDesktop = false,
  as = 'grid',
  onReady,
  onClose,
}: BaseProps) => {
  useEffect(() => {
    safeCall(onReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (items.length === 0) return null;

  const isBuyingToolsAndDesktop = isModelBuyingTools && isDesktop;

  if (as === 'expand') {
    return (
      <Wrapper>
        {isDesktop ? (
          <ToggleDesktop items={items} />
        ) : (
          <ToggleMobile items={items} />
        )}
      </Wrapper>
    );
  }

  const isSingle = items.length === 1;

  return (
    <Wrapper>
      {isSingle ? (
        <List
          item={items[0]}
          isDesktop={isDesktop}
          isModelBuyingTools={isModelBuyingTools}
          css={
            isBuyingToolsAndDesktop
              ? undefined
              : {
                  '@xl': {
                    gridColumn: '3 / span 4',
                  },
                }
          }
          onClose={onClose}
        />
      ) : (
        items.map((item, index) => (
          <Fragment key={`menu_list${index}`}>
            <List
              item={item}
              isDesktop={isDesktop}
              isModelBuyingTools={isModelBuyingTools}
              css={
                !isBuyingToolsAndDesktop
                  ? {
                      '@xl': {
                        gridColumn:
                          index % 2 === 0 ? '3 / span 4' : '7 / span 4',
                      },
                    }
                  : undefined
              }
              onClose={onClose}
            />
            {items.length - 1 > index && !isDesktop && (
              <Container size="mobile-only" maxWidth="none">
                {/* Tailwind <Divider size="xl" /> */}
                <Box
                  border="1"
                  marginY="8"
                  pointerEvents="none"
                  css={{
                    borderColor: '#ebebeb', // tailwind gray-50
                  }}
                />
              </Container>
            )}
          </Fragment>
        ))
      )}
    </Wrapper>
  );
};
